/**
 * Types and defaults for Trace Filter Configuration.
 * Mirrors audio-filter/types.ts but for trace spans instead of audio segments.
 */

import type {
  TraceFilterEntryConfig,
  TraceSpanAttributeFilterConfig,
  TraceSpanAttributeOperator,
  TraceSpanNameFilterConfig,
  TraceSpanNameOperator,
  TraceStatusFilterConfig,
  TraceStatusValue,
  TraceTimeRangeFilterConfig,
  TraceTurnRangeFilterConfig,
} from "@/types/metrics/metadata"

export type TraceFilterPreset = "full" | "custom"

export type TraceFilterEntryType = TraceFilterEntryConfig["type"]

/** Matches the Zod schema cap on include/exclude list length. */
export const MAX_TRACE_FILTERS_PER_CATEGORY = 5

export const TRACE_FILTER_TYPES: {
  value: TraceFilterEntryType
  label: string
  description: string
}[] = [
  {
    value: "span_name",
    label: "Span name",
    description: "Match spans whose name matches a literal or regex",
  },
  {
    value: "span_attribute",
    label: "Span attribute",
    description: "Match spans by a key/value on span_attributes",
  },
  {
    value: "time_range",
    label: "Time range",
    description: "First/last/middle N seconds of the trace",
  },
  {
    value: "turn_range",
    label: "Turn range",
    description: "First or last N matching spans",
  },
  {
    value: "status",
    label: "Status",
    description: "OTel status code (OK / ERROR / UNSET)",
  },
]

export const TRACE_SPAN_NAME_OPERATORS: {
  value: TraceSpanNameOperator
  label: string
}[] = [
  { value: "==", label: "equals" },
  { value: "!=", label: "not equals" },
  { value: "regex", label: "matches regex" },
]

export const TRACE_SPAN_ATTRIBUTE_OPERATORS: {
  value: TraceSpanAttributeOperator
  label: string
}[] = [
  { value: "==", label: "equals" },
  { value: "!=", label: "not equals" },
  { value: "regex", label: "matches regex" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "gte", label: ">=" },
  { value: "lte", label: "<=" },
]

export const TRACE_TIME_RANGE_MODES = [
  { value: "first_n_seconds" as const, label: "First" },
  { value: "last_n_seconds" as const, label: "Last" },
  { value: "middle_n_seconds" as const, label: "Middle" },
]

export const TRACE_TURN_RANGE_MODES = [
  { value: "first_n" as const, label: "First" },
  { value: "last_n" as const, label: "Last" },
]

export const TRACE_STATUS_VALUES: { value: TraceStatusValue; label: string }[] =
  [
    { value: "OK", label: "OK" },
    { value: "ERROR", label: "Error" },
    { value: "UNSET", label: "Unset" },
  ]

function generateFilterId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function createDefaultSpanNameFilter(): TraceSpanNameFilterConfig {
  return {
    type: "span_name",
    operator: "==",
    value: "llm",
    id: generateFilterId(),
  }
}

function createDefaultSpanAttributeFilter(): TraceSpanAttributeFilterConfig {
  return {
    type: "span_attribute",
    key: "",
    operator: "==",
    value: "",
    id: generateFilterId(),
  }
}

function createDefaultTimeRangeFilter(): TraceTimeRangeFilterConfig {
  return {
    type: "time_range",
    mode: "first_n_seconds",
    count: 30,
    id: generateFilterId(),
  }
}

function createDefaultTurnRangeFilter(): TraceTurnRangeFilterConfig {
  return {
    type: "turn_range",
    mode: "first_n",
    count: 3,
    id: generateFilterId(),
  }
}

function createDefaultStatusFilter(): TraceStatusFilterConfig {
  return { type: "status", value: "ERROR", id: generateFilterId() }
}

export function createDefaultTraceFilterEntry(
  type: TraceFilterEntryType,
): TraceFilterEntryConfig {
  switch (type) {
    case "span_name":
      return createDefaultSpanNameFilter()
    case "span_attribute":
      return createDefaultSpanAttributeFilter()
    case "time_range":
      return createDefaultTimeRangeFilter()
    case "turn_range":
      return createDefaultTurnRangeFilter()
    case "status":
      return createDefaultStatusFilter()
    default:
      throw new Error(`Unsupported trace filter type: ${String(type)}`)
  }
}
